export type MultiplierKind = 'normal' | 'start' | 'dl' | 'tl' | 'dp' | 'tp' | '4p' | 'cw';

export interface BoardCell {
  kind: MultiplierKind;
  letterMultiplier: number;
  wordMultiplier: number;
}

export type Multiplier = 'DL' | 'TL' | 'DP' | 'TP' | '4P' | 'ST' | 'CW' | BoardCell | null;

export const getMultiplierKind = (mult: Multiplier): string | null => {
  if (!mult) return null;
  if (typeof mult === 'string') return mult;
  return mult.kind.toUpperCase();
};

const MULTIPLIER_ALIASES: Record<string, Exclude<Multiplier, BoardCell | null>> = {
  DL: 'DL',
  DOUBLE_LETTER: 'DL',
  LETTER_DOUBLE: 'DL',
  TL: 'TL',
  TRIPLE_LETTER: 'TL',
  LETTER_TRIPLE: 'TL',
  DP: 'DP',
  DW: 'DP',
  DOUBLE_WORD: 'DP',
  WORD_DOUBLE: 'DP',
  TP: 'TP',
  TW: 'TP',
  TRIPLE_WORD: 'TP',
  WORD_TRIPLE: 'TP',
  '4P': '4P',
  QUADRUPLE_WORD: '4P',
  FOUR_WORD: '4P',
  ST: 'ST',
  START: 'ST',
  STAR: 'ST',
  CENTER: 'ST',
  CENTRE: 'ST',
  CW: 'CW',
  CENTER_WORD: 'CW'
};

const normalizeMultiplierToken = (value: unknown): Exclude<Multiplier, BoardCell | null> | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  return MULTIPLIER_ALIASES[normalized] || null;
};

export const normalizeMultiplier = (mult: unknown): Exclude<Multiplier, BoardCell> => {
  const direct = normalizeMultiplierToken(mult);
  if (direct) return direct;

  if (!mult || typeof mult !== 'object') return null;

  const raw = mult as Record<string, unknown>;
  const objectKind =
    normalizeMultiplierToken(raw.kind) ||
    normalizeMultiplierToken(raw.type) ||
    normalizeMultiplierToken(raw.bonus) ||
    normalizeMultiplierToken(raw.multiplier) ||
    normalizeMultiplierToken(raw.value);

  if (objectKind) return objectKind;

  if (raw.isCenter === true || raw.center === true || raw.isStart === true || raw.star === true) {
    return 'ST';
  }

  return null;
};

export const createEmptyBoard = <T = null>(size: number, value: T | null = null): (T | null)[][] => (
  Array(size).fill(null).map(() => Array(size).fill(value))
);

export const parseMatrix = (value: unknown): unknown[][] | null => {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') {
    if (typeof value !== 'object') return null;

    const rows = Object.entries(value as Record<string, unknown>)
      .filter(([key]) => /^\d+$/.test(key))
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, row]) => {
        if (Array.isArray(row)) return row;
        if (!row || typeof row !== 'object') return null;
        return Object.entries(row as Record<string, unknown>)
          .filter(([key]) => /^\d+$/.test(key))
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([, cell]) => cell);
      });

    return rows.length > 0 && rows.every(Array.isArray) ? rows as unknown[][] : null;
  }

  try {
    const parsed = JSON.parse(value);
    return parseMatrix(parsed);
  } catch {
    return null;
  }
};

export const isCompleteSquareMatrix = (matrix: unknown, size?: number): matrix is unknown[][] => {
  if (!Array.isArray(matrix) || matrix.length === 0) return false;
  const expectedSize = size || matrix.length;
  return matrix.length === expectedSize && matrix.every(row => Array.isArray(row) && row.length === expectedSize);
};

export const normalizeBoardLayout = (layout: unknown, size: number, fallback?: Multiplier[][]): Multiplier[][] => {
  const parsedLayout = parseMatrix(layout);
  const parsedFallback = parseMatrix(fallback);
  const source = isCompleteSquareMatrix(parsedLayout, size)
    ? parsedLayout
    : isCompleteSquareMatrix(parsedFallback, size)
      ? parsedFallback
      : createEmptyBoard(size);

  const normalized = source.map(row => row.map(cell => normalizeMultiplier(cell))) as Multiplier[][];
  const center = Math.floor(size / 2);
  if (normalized[center]) {
    normalized[center][center] = 'ST';
  }
  return normalized;
};

const setMultiplier = (board: Multiplier[][], coords: [number, number][], mult: Multiplier) => {
  const rows = board.length;
  const cols = board[0]?.length || 0;
  coords.forEach(([r, c]) => {
    // Handle potential out of bounds by capping or ignoring
    const targetR = Math.min(Math.max(0, r), rows - 1);
    const targetC = Math.min(Math.max(0, c), cols - 1);
    board[targetR][targetC] = mult;
  });
};

export const VERBO_BOARD: Multiplier[][] = Array(15).fill(null).map(() => Array(15).fill(null));

// CLÁSICO (15x15) Layout - Matches CLASICO - PLANTILLA
setMultiplier(VERBO_BOARD, [
  [0, 0], [0, 7], [0, 14], [7, 0], [7, 14], [14, 0], [14, 7], [14, 14]
], 'TP');
setMultiplier(VERBO_BOARD, [
  [1, 1], [2, 2], [3, 3], [4, 4], [1, 13], [2, 12], [3, 11], [4, 10], [13, 1], [12, 2], [11, 3], [10, 4], [13, 13], [12, 12], [11, 11], [10, 10]
], 'DP');
setMultiplier(VERBO_BOARD, [
  [1, 5], [1, 9], [5, 1], [5, 5], [5, 9], [5, 13], [9, 1], [9, 5], [9, 9], [9, 13], [13, 5], [13, 9]
], 'TL');
setMultiplier(VERBO_BOARD, [
  [0, 3], [0, 11], [2, 6], [2, 8], [3, 0], [3, 7], [3, 14], [6, 2], [6, 6], [6, 8], [6, 12], [7, 3], [7, 11], [8, 2], [8, 6], [8, 8], [8, 12], [11, 0], [11, 7], [11, 14], [12, 6], [12, 8], [14, 3], [14, 11]
], 'DL');
setMultiplier(VERBO_BOARD, [[7, 7]], 'ST');

export const STANDARD_BOARD: Multiplier[][] = Array(15).fill(null).map(() => Array(15).fill(null));

// Standard Layout (Updated to match CLASICO-PLANTILLA)
setMultiplier(STANDARD_BOARD, [
  [0, 0], [0, 7], [0, 14], [7, 0], [7, 14], [14, 0], [14, 7], [14, 14]
], 'TP');
setMultiplier(STANDARD_BOARD, [
  [1, 1], [2, 2], [3, 3], [4, 4], [1, 13], [2, 12], [3, 11], [4, 10], [13, 1], [12, 2], [11, 3], [10, 4], [13, 13], [12, 12], [11, 11], [10, 10]
], 'DP');
setMultiplier(STANDARD_BOARD, [
  [1, 5], [1, 9], [5, 1], [5, 5], [5, 9], [5, 13], [9, 1], [9, 5], [9, 9], [9, 13], [13, 5], [13, 9]
], 'TL');
setMultiplier(STANDARD_BOARD, [
  [0, 3], [0, 11], [2, 6], [2, 8], [3, 0], [3, 7], [3, 14], [6, 2], [6, 6], [6, 8], [6, 12], [7, 3], [7, 11], [8, 2], [8, 6], [8, 8], [8, 12], [11, 0], [11, 7], [11, 14], [12, 6], [12, 8], [14, 3], [14, 11]
], 'DL');
setMultiplier(STANDARD_BOARD, [[7, 7]], 'ST');

export const CLASSIC_BOARD: Multiplier[][] = Array(15).fill(null).map(() => Array(15).fill(null));

// Classic Layout (Matches CLASICO-PLANTILLA)
setMultiplier(CLASSIC_BOARD, [
  [0, 0], [0, 7], [0, 14], [7, 0], [7, 14], [14, 0], [14, 7], [14, 14]
], 'TP');
setMultiplier(CLASSIC_BOARD, [
  [1, 1], [2, 2], [3, 3], [4, 4], [1, 13], [2, 12], [3, 11], [4, 10], [13, 1], [12, 2], [11, 3], [10, 4], [13, 13], [12, 12], [11, 11], [10, 10]
], 'DP');
setMultiplier(CLASSIC_BOARD, [
  [1, 5], [1, 9], [5, 1], [5, 5], [5, 9], [5, 13], [9, 1], [9, 5], [9, 9], [9, 13], [13, 5], [13, 9]
], 'TL');
setMultiplier(CLASSIC_BOARD, [
  [0, 3], [0, 11], [2, 6], [2, 8], [3, 0], [3, 7], [3, 14], [6, 2], [6, 6], [6, 8], [6, 12], [7, 3], [7, 11], [8, 2], [8, 6], [8, 8], [8, 12], [11, 0], [11, 7], [11, 14], [12, 6], [12, 8], [14, 3], [14, 11]
], 'DL');
setMultiplier(CLASSIC_BOARD, [[7, 7]], 'ST');
export const CAOS_BOARD: Multiplier[][] = Array(15).fill(null).map(() => Array(15).fill(null));

// Populate CAOS_BOARD with a variety of multipliers that will be randomized
setMultiplier(CAOS_BOARD, [[0,0], [0,14], [14,0], [14,14]], '4P');
setMultiplier(CAOS_BOARD, [[0,7], [14,7], [7,0], [7,14]], 'TP');
setMultiplier(CAOS_BOARD, [[1,1], [1,13], [13,1], [13,13]], 'DP');
setMultiplier(CAOS_BOARD, [[2,2], [2,12], [12,2], [12,12]], 'TL');
setMultiplier(CAOS_BOARD, [[3,3], [3,11], [11,3], [11,11]], 'DL');
setMultiplier(CAOS_BOARD, [[4,4], [4,10], [10,4], [10,10]], 'CW');
setMultiplier(CAOS_BOARD, [[5,5], [5,9], [9,5], [9,9]], 'TP');
setMultiplier(CAOS_BOARD, [[6,6], [6,8], [8,6], [8,8]], 'DP');
setMultiplier(CAOS_BOARD, [[7,7]], 'ST');
export const RAPIDO_BOARD: Multiplier[][] = Array(13).fill(null).map(() => Array(13).fill(null));

// RÁPIDO (13x13) Layout - Matches 13x13 PLANTILLA
setMultiplier(RAPIDO_BOARD, [
  [0, 0], [0, 6], [0, 12], [6, 0], [6, 12], [12, 0], [12, 6], [12, 12]
], 'TP');
setMultiplier(RAPIDO_BOARD, [
  [1, 1], [2, 2], [3, 3], [4, 4], [1, 11], [2, 10], [3, 9], [4, 8], [11, 1], [10, 2], [9, 3], [8, 4], [11, 11], [10, 10], [9, 9], [8, 8]
], 'DP');
setMultiplier(RAPIDO_BOARD, [
  [1, 5], [1, 7], [5, 1], [5, 5], [5, 7], [5, 11], [7, 1], [7, 5], [7, 7], [7, 11], [11, 5], [11, 7]
], 'TL');
setMultiplier(RAPIDO_BOARD, [
  [0, 3], [0, 9], [2, 5], [2, 7], [3, 0], [3, 6], [3, 12], [5, 2], [5, 10], [6, 3], [6, 9], [7, 2], [7, 10], [9, 0], [9, 6], [9, 12], [10, 5], [10, 7], [12, 3], [12, 9]
], 'DL');
setMultiplier(RAPIDO_BOARD, [[6, 6]], 'ST');

export const MINI_BOARD: Multiplier[][] = Array(11).fill(null).map(() => Array(11).fill(null));

// MINI (11x11) Layout - Matches 11x11 - PLANTILLA
setMultiplier(MINI_BOARD, [
  [0, 0], [0, 5], [0, 10], [5, 0], [5, 10], [10, 0], [10, 5], [10, 10]
], 'TP');
setMultiplier(MINI_BOARD, [
  [1, 1], [2, 2], [3, 3], [1, 9], [2, 8], [3, 7], [9, 1], [8, 2], [7, 3], [9, 9], [8, 8], [7, 7]
], 'DP');
setMultiplier(MINI_BOARD, [
  [1, 4], [1, 6], [4, 1], [4, 4], [4, 6], [4, 9], [6, 1], [6, 4], [6, 6], [6, 9], [9, 4], [9, 6]
], 'TL');
setMultiplier(MINI_BOARD, [
  [0, 3], [0, 7], [2, 4], [2, 6], [3, 0], [3, 5], [3, 10], [5, 3], [5, 7], [7, 0], [7, 5], [7, 10], [8, 4], [8, 6], [10, 3], [10, 7]
], 'DL');
setMultiplier(MINI_BOARD, [[5, 5]], 'ST');

export const ESTRATEGICO_BOARD: Multiplier[][] = Array(15).fill(null).map(() => Array(15).fill(null));
export const DIAMANTE_BOARD: Multiplier[][] = Array(15).fill(null).map(() => Array(15).fill(null));

// DIAMANTE: Diamond shape multipliers
setMultiplier(DIAMANTE_BOARD, [
  [7, 0], [0, 7], [7, 14], [14, 7]
], '4P');
setMultiplier(DIAMANTE_BOARD, [
  [7, 3], [3, 7], [7, 11], [11, 7]
], 'TP');
setMultiplier(DIAMANTE_BOARD, [
  [7, 5], [5, 7], [7, 9], [9, 7]
], 'DP');
setMultiplier(DIAMANTE_BOARD, [
  [4, 4], [4, 10], [10, 4], [10, 10]
], 'TL');

// ESTRATÉGICO: Multipliers placed on edges and long diagonals
setMultiplier(ESTRATEGICO_BOARD, [
  [0,0], [0,14], [14,0], [14,14], [7,0], [0,7], [14,7], [7,14]
], '4P');
setMultiplier(ESTRATEGICO_BOARD, [
  [1,1], [1,13], [13,1], [13,13], [3,3], [3,11], [11,3], [11,11], [5,5], [5,9], [9,5], [9,9]
], 'TP');
setMultiplier(ESTRATEGICO_BOARD, [
  [2,2], [2,12], [12,2], [12,12], [4,4], [4,10], [10,4], [10,10], [6,6], [6,8], [8,6], [8,8]
], 'DP');

export const getBoardLayout = (type: string, size: number = 15, density: 'full' | 'light' | 'reduced' = 'full'): Multiplier[][] => {
  let baseLayout: Multiplier[][];
  
  if (size === 11 || type === 'MINI') baseLayout = MINI_BOARD;
  else if (size === 13 || type === 'RÁPIDO' || type === 'RAPIDO') baseLayout = RAPIDO_BOARD;
  else if (type === 'VERBO') baseLayout = VERBO_BOARD;
  else if (type === 'NO TOCAR' || type === 'Fijo' || type === 'Clásico') baseLayout = STANDARD_BOARD;
  else if (type === 'CAOS') baseLayout = CAOS_BOARD;
  else if (type === 'ESTRATÉGICO') baseLayout = ESTRATEGICO_BOARD;
  else if (type === 'DIAMANTE') baseLayout = DIAMANTE_BOARD;
  else if (type === 'ANIVERSARIO' || type === 'TORNEO') baseLayout = STANDARD_BOARD;
  else if (type === 'PERSONAL') baseLayout = STANDARD_BOARD;
  else baseLayout = STANDARD_BOARD;

  // 1. Handle Size
  let board: Multiplier[][];
  if (size <= 0) {
    return Array(15).fill(null).map(() => Array(15).fill(null));
  }
  if (baseLayout.length !== size) {
    // Create a new board of the requested size
    board = Array(size).fill(null).map(() => Array(size).fill(null));
    
    // Map multipliers from source size to the new size (centered)
    const sourceSize = baseLayout.length;
    const offset = Math.floor((sourceSize - size) / 2);
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const sourceR = r + offset;
        const sourceC = c + offset;
        if (sourceR >= 0 && sourceR < sourceSize && sourceC >= 0 && sourceC < sourceSize) {
          board[r][c] = baseLayout[sourceR][sourceC];
        }
      }
    }
    
    // Ensure the center is ST
    const center = Math.floor(size / 2);
    board[center][center] = 'ST';
  } else {
    board = baseLayout.map(row => [...row]);
  }

  // 2. Handle Density
  if (density !== 'full') {
    const center = size / 2;
    const maxDist = Math.sqrt(Math.pow(center, 2) + Math.pow(center, 2));
    
    // Thresholds for density
    // light: ~60% (remove furthest)
    // reduced: ~40% (only closest)
    const threshold = density === 'light' ? 0.7 : 0.4;

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const mult = board[r][c];
        if (mult && mult !== 'ST') {
          const dist = Math.sqrt(Math.pow(r + 0.5 - center, 2) + Math.pow(c + 0.5 - center, 2));
          const normalizedDist = dist / maxDist;
          
          if (normalizedDist > threshold) {
            board[r][c] = null;
          }
        }
      }
    }
  }

  // 3. Special types (Turbo, Aleatorio) - only if 15x15 for simplicity or adapt
  if (type === 'Turbo' || type === 'INTENSO') {
    const q1 = Math.floor(size / 4);
    const q3 = size - q1 - 1;
    const mid = Math.floor(size / 2);
    if (q1 < size && q3 < size) {
      board[q1][mid] = '4P';
      board[q3][mid] = '4P';
      board[mid][q1] = '4P';
      board[mid][q3] = '4P';
    }
  }
  
  if (type === 'Aleatorio' || type === 'ALEATORIO' || type === 'CAOS') {
    const allMultipliers: Multiplier[] = [];
    const center = Math.floor(size / 2);
    
    board.forEach((row, r) => {
      row.forEach((mult, c) => {
        if (mult && !(r === center && c === center)) {
          allMultipliers.push(mult);
        }
      });
    });

    for (let i = allMultipliers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allMultipliers[i], allMultipliers[j]] = [allMultipliers[j], allMultipliers[i]];
    }

    let idx = 0;
    board = board.map((row, r) => 
      row.map((mult, c) => {
        if (mult && !(r === center && c === center)) {
          return allMultipliers[idx++];
        }
        return mult;
      })
    );
  }
  
  return board;
};
